import {
  ensureGuestSessionId,
  readStorageValue,
  safeJsonParse,
  writeStorageValue,
} from '../security';

const DEFAULT_GUEST_DAILY_IMAGE_LIMIT = 5;
const parsedGuestDailyImageLimit = Number(import.meta.env.VITE_GUEST_DAILY_IMAGE_LIMIT);
const GUEST_DAILY_IMAGE_LIMIT =
  Number.isFinite(parsedGuestDailyImageLimit) && parsedGuestDailyImageLimit > 0
    ? Math.trunc(parsedGuestDailyImageLimit)
    : DEFAULT_GUEST_DAILY_IMAGE_LIMIT;
const GUEST_IMAGE_USAGE_STORAGE_KEY = 'vadauro_guest_image_generation_usage';

interface GuestImageGenerationUsageRecord {
  count: number;
  sessionId: string;
}

interface GuestImageGenerationLimitState {
  isLimitReached: boolean;
  limit: number;
  remaining: number;
  used: number;
}

function sanitizeUsageRecord(value: unknown): GuestImageGenerationUsageRecord | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const sessionId = typeof candidate.sessionId === 'string' ? candidate.sessionId.trim() : '';
  const rawCount = typeof candidate.count === 'number' ? candidate.count : Number(candidate.count);
  const count = Number.isFinite(rawCount) && rawCount > 0 ? Math.trunc(rawCount) : 0;

  if (!sessionId) {
    return null;
  }

  return {
    count,
    sessionId,
  };
}

function readCurrentUsageRecord() {
  const sessionId = ensureGuestSessionId();
  const storedValue = readStorageValue(GUEST_IMAGE_USAGE_STORAGE_KEY, { storage: 'local' });
  const parsedValue = storedValue
    ? sanitizeUsageRecord(safeJsonParse<unknown>(storedValue, null))
    : null;

  if (!parsedValue || parsedValue.sessionId !== sessionId) {
    return {
      count: 0,
      sessionId,
    };
  }

  return parsedValue;
}

function persistUsageRecord(record: GuestImageGenerationUsageRecord) {
  writeStorageValue(GUEST_IMAGE_USAGE_STORAGE_KEY, JSON.stringify(record), { storage: 'local' });
}

export function getGuestImageGenerationLimitState(): GuestImageGenerationLimitState {
  const usageRecord = readCurrentUsageRecord();
  const used = Math.max(0, Math.min(usageRecord.count, GUEST_DAILY_IMAGE_LIMIT));
  const remaining = Math.max(0, GUEST_DAILY_IMAGE_LIMIT - used);

  return {
    isLimitReached: remaining <= 0,
    limit: GUEST_DAILY_IMAGE_LIMIT,
    remaining,
    used,
  };
}

export function recordGuestImageGeneration(usedCount = 1) {
  const usageRecord = readCurrentUsageRecord();
  const nextUsedCount = Number.isFinite(usedCount) ? Math.max(1, Math.trunc(usedCount)) : 1;
  const nextRecord = {
    ...usageRecord,
    count: Math.min(usageRecord.count + nextUsedCount, GUEST_DAILY_IMAGE_LIMIT),
  };

  persistUsageRecord(nextRecord);

  return getGuestImageGenerationLimitState();
}
